import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useEnrollmentCart } from '@/components/enrollment/EnrollmentCartContext';
import CoverageLevelModal from '@/components/enrollment/CoverageLevelModal';
import EnrollmentAIChat from '@/components/ai/EnrollmentAIChat';
import { isFeatureEnabled } from '@/components/utils/featureFlags';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Filter,
  Eye,
  Sparkles,
  DollarSign,
  ShoppingCart,
  Plus,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateBenefitPremium } from '@/components/utils/benefitPricing';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import { toast } from 'sonner';

function TaxSavingsBadge({ productId, employee }) {
  const [savings, setSavings] = useState(null);
  
  useEffect(() => {
    const calc = async () => {
      try {
        const products = await api.entities.BenefitProduct.filter({ id: productId });
        if (products.length > 0) {
          const premium = calculateBenefitPremium(products[0], employee, [], 'employee_only');
          const taxAnalysis = await calculateEmployeeTaxes(employee, premium * 12);
          setSavings(taxAnalysis.savings.total);
        }
      } catch {}
    };
    calc();
  }, [productId, employee]);
  
  if (!savings || savings <= 0) return null;
  
  return (
    <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">
      ðŸ’° Save ~${savings.toFixed(0)}/year in taxes
    </div>
  );
}

export default function BenefitsCatalog() {
  const { addToCart, isInCart, itemCount } = useEnrollmentCart();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [aiChatEnabled, setAiChatEnabled] = useState(true);

  useEffect(() => {
    loadData();
    checkFeatures();
  }, []);

  useEffect(() => {
    const persistStep = async () => {
      if (employee?.id) {
        try {
          await api.entities.Employee.update(employee.id, { last_enrollment_step: 'select' });
        } catch (err) {
          console.error('Failed to persist enrollment select step', err);
        }
      }
    };
    persistStep();
  }, [employee?.id]);

  const checkFeatures = async () => {
    const aiEnabled = await isFeatureEnabled('ai_chat_enabled');
    setAiChatEnabled(aiEnabled);
  };

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      const [productsData, categoriesData] = await Promise.all([
        api.entities.BenefitProduct.filter({ is_active: true }),
        api.entities.BenefitCategory.filter({ is_active: true })
      ]);
      
      // Parse JSON fields for each product
      const parsedProducts = productsData.map(product => {
        let features = product.features || [];
        if (typeof features === 'string') {
          try {
            features = JSON.parse(features);
          } catch (e) {
            console.error('Failed to parse features for product:', product.name, e);
            features = [];
          }
        }
        
        let faq = product.faq || [];
        if (typeof faq === 'string') {
          try {
            faq = JSON.parse(faq);
          } catch (e) {
            console.error('Failed to parse faq for product:', product.name, e);
            faq = [];
          }
        }
        
        return {
          ...product,
          features,
          faq
        };
      });
      
      setProducts(parsedProducts);
      setCategories(categoriesData);
      
      if (employees.length > 0) {
        setEmployee(employees[0]);
        const [family, employeeEnrollments] = await Promise.all([
          api.entities.FamilyMember.filter({ 
            employee_id: employees[0].id,
            is_active: true
          }),
          api.entities.Enrollment.filter({
            employee_id: employees[0].id,
            status: 'active'
          })
        ]);
        setFamilyMembers(family);
        setEnrollments(employeeEnrollments);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const isEnrolled = (productId) => {
    return enrollments.some(e => e.product_id === productId);
  };

  const handleAddToCart = (product, coverageLevel, premium) => {
    addToCart(product, coverageLevel, premium);
    toast.success(`${product.name} added to enrollment!`);
  };

  const openCoverageModal = (product) => {
    setSelectedProduct(product);
    setShowCoverageModal(true);
  };

  const calculateTaxSavings = async (product) => {
    if (!employee) return null;
    try {
      const premium = calculateBenefitPremium(product, employee, [], 'employee_only');
      const annualPremium = premium * 12;
      const taxAnalysis = await calculateEmployeeTaxes(employee, annualPremium);
      return taxAnalysis.savings.total;
    } catch {
      return null;
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.short_description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
    
    let matchesPrice = true;
    const premium = parseFloat(product.base_premium || 0);
    if (priceFilter === 'low') matchesPrice = premium < 50;
    else if (priceFilter === 'medium') matchesPrice = premium >= 50 && premium < 150;
    else if (priceFilter === 'high') matchesPrice = premium >= 150;
    
    return matchesSearch && matchesCategory && matchesPrice;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'General';
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.icon || 'Heart';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasMissingPricingData = employee && (!employee.date_of_birth || !employee.gender);

  return (
    <div className="space-y-8">
      {/* Missing Data Warning */}
      {hasMissingPricingData && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
          <CardContent className="p-4 flex items-start gap-4">
            <HelpCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900 mb-1">Incomplete Profile - Pricing May Be Inaccurate</p>
              <p className="text-sm text-amber-800 mb-3">
                We're missing some information needed to calculate accurate benefit prices. 
                Please update your profile with your date of birth and gender for personalized pricing.
              </p>
              <Link to={createPageUrl('EmployeeProfile')}>
                <Button size="sm" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  Complete My Profile
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Benefits Catalog</h1>
          <p className="text-slate-500 mt-1">
            Explore our comprehensive range of employee benefits
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-emerald-100 text-emerald-700 w-fit">
            <Sparkles className="w-3 h-3 mr-1" />
            {products.length} Benefits Available
          </Badge>
          {itemCount > 0 && (
            <Link to={createPageUrl('EnrollmentReview')}>
              <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Review ({itemCount})
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search benefits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={priceFilter} onValueChange={setPriceFilter}>
              <SelectTrigger className="w-full md:w-48">
                <DollarSign className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Price Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="low">Under $50/mo</SelectItem>
                <SelectItem value="medium">$50 - $150/mo</SelectItem>
                <SelectItem value="high">Over $150/mo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      <AnimatePresence mode="popLayout">
        {filteredProducts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Benefits Found</h3>
            <p className="text-slate-500">Try adjusting your search or filters</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 group overflow-hidden border-slate-200">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={product.image_url || `https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop`}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <Badge className="absolute top-4 left-4 bg-white/90 text-slate-700">
                      {getCategoryName(product.category_id)}
                    </Badge>
                    {product.video_url && (
                      <Badge className="absolute top-4 right-4 bg-red-500 text-white">
                        <Eye className="w-3 h-3 mr-1" />
                        Video
                      </Badge>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white">{product.name}</h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-6 space-y-4">
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {product.short_description || 'Comprehensive coverage for you and your family.'}
                    </p>
                    
                    {/* Features */}
                    {product.features && product.features.length > 0 && (
                      <div className="space-y-2">
                        {product.features.slice(0, 3).map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Price & Action */}
                    <div className="space-y-3 pt-4 border-t border-slate-100">
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Starting from</p>
                          <p className="text-2xl font-bold text-slate-900">
                            ${parseFloat(product.base_premium || 0).toFixed(2)}
                            <span className="text-sm font-normal text-slate-500">/mo</span>
                          </p>
                        </div>
                      </div>
                      
                      {employee && (
                        <TaxSavingsBadge productId={product.id} employee={employee} />
                      )}
                      
                      <div className="flex gap-2">
                        <Link to={createPageUrl(`BenefitDetail?id=${product.id}`)} className="flex-1">
                          <Button variant="outline" className="w-full">
                            <Eye className="w-4 h-4 mr-2" />
                            Details
                          </Button>
                        </Link>
                        {isEnrolled(product.id) ? (
                          <Button 
                            disabled
                            className="flex-1 bg-emerald-600"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Enrolled
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => openCoverageModal(product)}
                            disabled={isInCart(product.id)}
                            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                          >
                            {isInCart(product.id) ? (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Added
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Add
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Coverage Level Modal */}
      <CoverageLevelModal
        open={showCoverageModal}
        onClose={() => setShowCoverageModal(false)}
        product={selectedProduct}
        employee={employee}
        familyMembers={familyMembers}
        onAddToCart={handleAddToCart}
      />

      {/* AI Chat Widget */}
      {aiChatEnabled && (
        <EnrollmentAIChat 
          context={{ 
            currentPage: 'catalog', 
            employeeData: employee,
            availableProducts: products,
            selectedCategory: selectedCategory
          }} 
        />
      )}
    </div>
  );
}